import React, { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import { GetApp } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "commons/store/hooks";
import ColoredBox from "components/ColoredBox";
import { Table } from "components/Table";
import { useSnackbar } from "notistack";
import { selectUserProfile } from "core/redux/userSlice";
import { roles } from 'commons/auth/roles';
import { useTranslation } from "react-i18next";
import { RowDetails } from "./components";
import { fetchEcrfDataTransfers } from "../../redux/dashboardSlice";
import { MIME_TYPE } from "utils/dataAccess";
import { downloadDataTransfer } from "../../../ecrf/redux/ecrfDataTransferManagementSlice";
import DateTime from "../../../../core/helpers/date/DateTime";
import {DATETIME_FORMAT} from "../../../../commons/constants/env";

const DashboardResourceTransfers = () => {
    const dispatch = useAppDispatch();
    const [page, setPage] = useState(1);
    const { enqueueSnackbar } = useSnackbar();
    const [isDataLoading, setIsDataLoading] = useState(false)
    const userProfile = useAppSelector(selectUserProfile);
    const { t } = useTranslation();

    const [collection, setCollection] = useState<any>({
        itemsPerPage: 0,
        totalItems: 0,
        member: []
    })

    const fetchData = async () => {
        setIsDataLoading(true);

        const config = {
          headers: {
            Accept: MIME_TYPE,
          },
          params: {
            pagination: true,
            page,
            itemsPerPage: 30,
          },
        };

        try {
            const response = await dispatch(fetchEcrfDataTransfers(config)).unwrap();
            setCollection(response);
        } catch (error: any) {
            enqueueSnackbar(error?.message || error?.detail, { variant: 'error' });
        }

        setIsDataLoading(false);
    }

    const isReadOnly: boolean = userProfile.roles.some(
        (role: string) => roles.monitor.includes(role)
    );

    const ecrfStatusClosed = (status: number) => {
        switch (status) {
            case 1:
            case 2:
            case 6:
            case 5:
                return true;
            default:
                return false;
        }
    }

    const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set());

    useEffect(() => {
        fetchData();
    }, [dispatch, page]);

    return (
        <ColoredBox pt={0} px={5} maxWidth="100%" ml="auto" mr="auto">
            <Table
                pagination={{
                    pageSize: collection?.itemsPerPage || 10,
                    current: page,
                    total: collection?.totalItems,
                }}
                onChangePage={(page) => setPage(page)}
                data={collection?.['member'] ?? []}
                isLoading={isDataLoading}
                isOpenable={true}
                tableName={t('Resource transfers')}
                columns={[
                    {
                        title: <Box ml="20px">{t('screening-number')}</Box>,
                        render: (row: any) => (
                            <Box
                                p={1}
                                style={{
                                  opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%',
                                  backgroundColor: row?.drugs ? "" : "#6BDFE1",
                                  borderRadius: row?.drugs ? "" : "5px",
                                  fontWeight: row?.drugs ? 400 : 700,
                                  textAlign: "center",
                                  minWidth: 60
                                }}
                            >
                                {row?.screeningNumber}
                            </Box>
                        ),
                        key: 'screeningNumber',
                    },
                    {
                        title: <Box ml="20px">{t('Total items')}</Box>,
                        render: (row: any) => (
                            <Box
                                p={1}
                                style={{ opacity: ecrfStatusClosed(row?.status) ? '50%' : '100%' }}
                            >
                                {row?.elements?.length || 0}
                            </Box>
                        ),
                        key: 'screeningNumber',
                    },
                    {
                      title: <Box ml="20px">{t('created-at')}</Box>,
                      render: (row: any) => (
                        <Box mt="20px" mb="20px">
                          {DateTime.toIso(row?.createdAt, DATETIME_FORMAT)}
                        </Box>
                      ),
                      key: 'updatedAt',
                    },
                    {
                      title: <Box ml="20px">{t('actions')}</Box>,
                      noWrap: true,
                      render: (row: any) => {
                        const handleDownload = async () => {
                          setLoadingIds(prev => new Set(prev).add(row.id));
                          try {
                            await dispatch(downloadDataTransfer(row.id)).unwrap();
                          } catch (e) {
                            enqueueSnackbar(t('error'), { variant: 'error' });
                          } finally {
                            setLoadingIds(prev => {
                              const next = new Set(prev);
                              next.delete(row.id);
                              return next;
                            });
                          }
                        };

                        return (
                          <Box>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              disabled={row?.elements?.length === 0}
                              onClick={handleDownload}
                              loading={loadingIds.has(row.id)}
                            >
                              <GetApp />
                            </Button>
                          </Box>
                        );
                      },
                      key: 'action',
                    },
                ]}
                maxHeight={600}
                rowDetails={(row, index) => (
                  <RowDetails row={row} />
                )}
            />
        </ColoredBox>
    );
};

export default DashboardResourceTransfers;
